import React, { useState, useEffect } from 'react';
import {
  FaHome,
  FaUtensils,
  FaUser,
  FaMotorcycle,
  FaShoppingBag,
  FaHeart,
  FaBars,
  FaTimes,
} from 'react-icons/fa';
import { useDispatch, useSelector } from 'react-redux';
import { NavLink, useNavigate } from 'react-router-dom';
import {
  getCurrentTenantDisplayInfo,
  getCurrentUser,
  getTenantDisplayInfo,
  logoutUser,
} from '../redux/auth/authSlice';

// Components & Helpers
import { getImagePath, isDarkSwitchActive } from '../constants/defaultValues';
import { useThemeColors } from '../hooks/useThemeColors';
import { toast } from 'react-toastify';
import { getAvailableGetDarkMode, setDarkMode } from '../redux/pos/posSlice';
import { getAvailableCartItems } from '../redux/addCart/cartSlice';

const Navbar = ({ onOpenTracker, onOpenDelivery, onOpenLogin, onOpenCart }) => {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const cartItems = useSelector(getAvailableCartItems);
  const [scrolled, setScrolled] = useState(false);
  const [activeDelivery, setActiveDelivery] = useState('delivery');
  const [sidebarOpen, setSidebarOpen] = useState(false);

  const currentTenant = useSelector(getCurrentTenantDisplayInfo);
  const user = useSelector(getCurrentUser);
  const isDark = useSelector(getAvailableGetDarkMode);

  const { textColor, subTextColor, borderColor, headerBg, contentBg } =
    useThemeColors();

  const navHeight = scrolled ? '65px' : '80px';
  const cartCount =
    cartItems?.reduce((total, item) => total + (item.quantity || 1), 0) || 0;

  useEffect(() => {
    const handleScroll = () => setScrolled(window.scrollY > 20);
    window.addEventListener('scroll', handleScroll);
    dispatch(getTenantDisplayInfo());
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  const menuItems = [
    { label: 'Explore Menu', icon: <FaUtensils />, path: '/', type: 'route' },
    {
      label: 'Store Locator',
      icon: <FaHome />,
      path: '/storeLocator',
      type: 'route',
    },
    {
      label: 'Track Order',
      icon: <FaUtensils />,
      action: onOpenTracker,
      type: 'popup',
    },
    {
      label: 'My Orders',
      icon: <FaShoppingBag />,
      path: '/myOrders',
      type: 'route',
    },
    {
      label: 'My Favourite',
      icon: <FaHeart />,
      path: '/myFavourite',
      type: 'route',
    },
  ];

  const DeliverySwitcher = ({ isMobile = false }) => (
    <div
      className="d-flex p-1 rounded transition-all"
      style={{
        backgroundColor: isDark ? 'rgba(255,255,255,0.05)' : '#f8f9fa',
        border: `1px solid ${borderColor}`,
      }}
    >
      <button
        className={`btn btn-sm rounded px-sm-3 fw-bold flex-fill transition-all ${
          activeDelivery === 'delivery' ? 'btn-danger shadow' : 'border-0'
        }`}
        style={{
          color:
            activeDelivery === 'delivery'
              ? '#fff'
              : isDark
              ? subTextColor
              : '#6c757d',
        }}
        onClick={() => {
          setActiveDelivery('delivery');
          onOpenDelivery();
        }}
      >
        <FaMotorcycle className="me-2" /> DELIVERY
      </button>

      <button
        className={`btn btn-sm rounded px-sm-3 fw-bold flex-fill transition-all ${
          activeDelivery === 'pickup' ? 'btn-danger shadow' : 'border-0'
        }`}
        style={{
          color:
            activeDelivery === 'pickup'
              ? '#fff'
              : isDark
              ? subTextColor
              : '#6c757d',
        }}
        onClick={() => {
          setActiveDelivery('pickup');
          if (isMobile) setSidebarOpen(false);
        }}
      >
        <FaShoppingBag className="me-2" /> PICKUP
      </button>
    </div>
  );

  return (
    <>
      <nav
        className={`navbar fixed-top transition-all ${
          scrolled ? 'shadow-sm py-2' : 'py-3'
        }`}
        style={{
          zIndex: 1150,
          height: navHeight,
          backgroundColor: headerBg,
          borderBottom: `1px solid ${borderColor}`,
          transition: 'all 0.3s ease',
        }}
      >
        <div className="container-fluid px-lg-5 px-3">
          <div className="d-flex align-items-center">
            <button
              className="btn border-0 p-0 me-sm-4"
              onClick={() => setSidebarOpen(!sidebarOpen)}
            >
              <FaBars size={24} style={{ color: textColor }} />
            </button>

            {currentTenant && (
              <NavLink className="navbar-brand p-0 m-0" to="/">
                <img
                  src={`${getImagePath}${currentTenant.logoId}`}
                  alt="Logo"
                  height="50"
                  style={{ objectFit: 'contain' }}
                />
              </NavLink>
            )}
          </div>

          <div className="d-flex align-items-center gap-2 gap-md-4">
            <div
              className="d-flex d-lg-none justify-content-center align-items-center rounded-circle shadow-sm position-relative"
              style={{
                backgroundColor: contentBg,
                width: '45px',
                height: '45px',
                color: textColor,
                cursor: 'pointer',
              }}
              onClick={onOpenCart}
            >
              <FaShoppingBag size={20} />
              {cartCount > 0 && (
                <span
                  className="position-absolute badge rounded-pill bg-danger"
                  style={{
                    top: '-4px',
                    right: '-4px',
                    fontSize: '0.65rem',
                    minWidth: '18px',
                    height: '18px',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    padding: '0 4px',
                  }}
                >
                  {cartCount}
                </span>
              )}
            </div>
            <div className="d-none d-sm-block">
              <DeliverySwitcher isMobile={false} />
            </div>
            {!user?.id && (
              <button
                className="btn btn-danger fw-bold rounded-pill px-4 py-2 text-uppercase"
                onClick={onOpenLogin} // Yahan change kiya: navigate ki jagah popup function call kiya
              >
                Login
              </button>
            )}
          </div>
        </div>
      </nav>

      {/* --- SIDEBAR --- */}
      <div
        className={`offcanvas offcanvas-start shadow-lg ${
          sidebarOpen ? 'show' : ''
        }`}
        style={{
          visibility: sidebarOpen ? 'visible' : 'hidden',
          width: '300px',
          transition: '0.3s ease',
          display: 'flex',
          flexDirection: 'column',
          maxWidth: '85vw',
          height: `calc(100vh - ${navHeight})`,
          marginTop: navHeight,
          position: 'fixed',
          top: 0,
          zIndex: 1100,
          backgroundColor: headerBg,
          borderRight: `1px solid ${borderColor}`,
          color: textColor,
        }}
      >
        {/* 1. Fixed Header */}
        <div
          className="offcanvas-header px-4 py-4 d-flex justify-content-between align-items-center"
          style={{ borderColor: borderColor }}
        >
          <h5 className="mb-0 fw-bold" style={{ color: textColor }}>
            Menu
          </h5>
          <button
            className="btn p-0 border-0"
            onClick={() => setSidebarOpen(false)}
          >
            <FaTimes size={22} className="text-danger" />
          </button>
        </div>

        {/* 2. Scrollable Container (Isme Items + Bottom Section dono hain) */}
        <div
          className="offcanvas-body p-0 d-flex flex-column"
          style={{ overflowY: 'auto', overflowX: 'hidden' }}
        >
          <div className="flex-shrink-0">
            <div className="px-4 py-3 d-sm-none">
              <DeliverySwitcher isMobile={true} />
            </div>

            <ul className="list-group list-group-flush bg-transparent">
              {menuItems.map((item, idx) => (
                <li
                  key={idx}
                  className="list-group-item border-0 p-0 bg-transparent"
                >
                  {/* ... aapka existing NavLink/Button logic ... */}
                  {item.type === 'route' ? (
                    <NavLink
                      to={item.path}
                      className={({ isActive }) =>
                        `sidebar-link d-flex align-items-center text-decoration-none px-4 py-3 ${
                          isActive ? 'bg-danger text-white shadow-sm' : ''
                        }`
                      }
                      style={({ isActive }) => ({
                        color: isActive ? '#fff' : textColor,
                        transition: '0.2s all',
                      })}
                      onClick={() => setSidebarOpen(false)}
                    >
                      <span className="me-3" style={{ fontSize: '1.1rem' }}>
                        {item.icon}
                      </span>
                      <span className="fw-medium">{item.label}</span>
                    </NavLink>
                  ) : (
                    <button
                      className="sidebar-link d-flex align-items-center border-0 bg-transparent w-100 px-4 py-3 text-start"
                      style={{ color: textColor, transition: '0.2s all' }}
                      onClick={() => {
                        if (user) item.action();
                        else {
                          toast.error('Please Login First');
                          onOpenLogin();
                        }
                        setSidebarOpen(false);
                      }}
                    >
                      <span
                        className="me-3 text-danger"
                        style={{ fontSize: '1.1rem' }}
                      >
                        {item.icon}
                      </span>
                      <span className="fw-medium">{item.label}</span>
                    </button>
                  )}
                </li>
              ))}
            </ul>
          </div>

          {/* 3. Bottom Section (Ab ye body ka hissa hai, isliye items ke sath scroll hoga) */}
          <div className="p-4 mt-auto" style={{ borderColor: borderColor }}>
            {isDarkSwitchActive && (
              <div
                className="mode-toggle d-flex align-items-center justify-content-between p-3 rounded mb-3"
                style={{
                  backgroundColor: isDark
                    ? 'rgba(255,255,255,0.05)'
                    : 'rgba(0,0,0,0.03)',
                }}
              >
                <span
                  className="small fw-bold text-uppercase"
                  style={{ color: subTextColor }}
                >
                  Dark Mode
                </span>
                <div className="form-check form-switch m-0">
                  <input
                    className="form-check-input cursor-pointer"
                    type="checkbox"
                    checked={isDark}
                    onChange={() => dispatch(setDarkMode(!isDark))}
                    style={{ width: '2.5em', height: '1.2em' }}
                  />
                </div>
              </div>
            )}

            <button
              className="btn btn-danger w-100 py-3 fw-bold text-uppercase d-flex align-items-center justify-content-center gap-2 shadow-sm"
              onClick={() => {
                if (user?.id) {
                  dispatch(logoutUser());
                  navigate('/');
                } else {
                  onOpenLogin();
                }
                setSidebarOpen(false);
              }}
            >
              <FaUser /> {user?.id ? 'Logout' : 'Login'}
            </button>
          </div>
        </div>
      </div>

      {sidebarOpen && (
        <div
          className="modal-backdrop fade show"
          style={{ zIndex: 1040, top: navHeight }}
          onClick={() => setSidebarOpen(false)}
        ></div>
      )}
    </>
  );
};

export default Navbar;
